import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FIELD_TYPE_MAP,
  isCommonBasicSection,
  getSectionServices,
} from "./boardingFormTemplate";

const inputClass =
  "w-full border border-gray-300 rounded-lg px-3.5 py-3 text-sm bg-white text-gray-900 cursor-default";

function optLabel(o) {
  return typeof o === "string"
    ? o
    : o?.label || o?.value || o?.optionText || o?.name || "";
}

function FieldPreview({ field }) {
  const {
    fieldType,
    label,
    placeholder,
    helpText,
    required,
    options,
    defaultValue,
    enabled,
  } = field;
  if (!enabled) return null;

  const labelEl = (
    <label className="block text-[13px] font-semibold text-gray-800 mb-1.5">
      {label || <span className="text-gray-400 italic">Untitled Field</span>}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
  const helpEl = helpText ? (
    <p className="text-xs text-gray-500 mt-1.5">{helpText}</p>
  ) : null;

  const pills = (opts, multi = false) => (
    <div className="flex flex-wrap gap-2">
      {(opts || []).map((o, i) => (
        <span
          key={i}
          className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800"
        >
          {optLabel(o) || `Option ${i + 1}`}
        </span>
      ))}
      {(!opts || opts.length === 0) && (
        <>
          <span className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-400">
            Option 1
          </span>
          <span className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-400">
            Option 2
          </span>
        </>
      )}
    </div>
  );

  switch (fieldType) {
    case "short_text":
    case "email":
    case "phone":
    case "url":
    case "number":
      return (
        <div className="ff-field">
          {labelEl}
          <input
            readOnly
            type="text"
            placeholder={placeholder || ""}
            defaultValue={defaultValue || ""}
            className={inputClass}
          />
          {helpEl}
        </div>
      );
    case "long_text":
      return (
        <div className="ff-field ff-full">
          {labelEl}
          <textarea
            readOnly
            rows={3}
            placeholder={placeholder || ""}
            defaultValue={defaultValue || ""}
            className={`${inputClass} resize-none`}
          />
          {helpEl}
        </div>
      );
    case "dropdown":
      return (
        <div className="ff-field">
          {labelEl}
          <select disabled className={inputClass}>
            <option>{placeholder || "Select an option"}</option>
            {(options || []).map((o, i) => (
              <option key={i}>{optLabel(o)}</option>
            ))}
          </select>
          {helpEl}
        </div>
      );
    case "single_choice":
    case "yes_no":
    case "checkbox":
    case "multiple_choice":
    case "vaccination_details": {
      const opts =
        fieldType === "yes_no"
          ? [{ label: "Yes" }, { label: "No" }]
          : fieldType === "vaccination_details" && field.vaccinationList?.length
            ? field.vaccinationList
            : options;
      return (
        <div className="ff-field ff-full">
          {labelEl}
          {pills(opts, fieldType !== "single_choice" && fieldType !== "yes_no")}
          {helpEl}
        </div>
      );
    }
    case "date_picker":
      return (
        <div className="ff-field">
          {labelEl}
          <input type="date" disabled className={inputClass} />
          {helpEl}
        </div>
      );
    case "time_picker":
      return (
        <div className="ff-field">
          {labelEl}
          <input type="time" disabled className={inputClass} />
          {helpEl}
        </div>
      );
    case "datetime_picker":
      return (
        <div className="ff-field">
          {labelEl}
          <input type="datetime-local" disabled className={inputClass} />
          {helpEl}
        </div>
      );
    case "file_upload":
    case "multi_file_upload":
    case "image_upload":
      return (
        <div className="ff-field ff-full">
          {labelEl}
          <div className="border border-dashed border-gray-300 rounded-xl p-5 text-center bg-white">
            <p className="text-sm text-gray-500">
              {fieldType === "image_upload"
                ? "Upload image"
                : "Drop files here or browse"}
            </p>
          </div>
          {helpEl}
        </div>
      );
    case "signature":
      return (
        <div className="ff-field ff-full">
          {labelEl}
          <div className="border border-gray-300 rounded-xl h-28 bg-white flex items-center justify-center">
            <p className="text-xs text-gray-400">Sign here</p>
          </div>
          {helpEl}
        </div>
      );
    case "section_heading":
      return (
        <div className="ff-field ff-full pt-1 pb-1 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 m-0">
            {defaultValue || label}
          </h2>
        </div>
      );
    case "description_text":
      return (
        <div className="ff-field ff-full">
          <p className="text-sm leading-relaxed text-gray-600 m-0">
            {defaultValue || label || "Description text will appear here."}
          </p>
        </div>
      );
    case "address":
      return (
        <div className="ff-field ff-full">
          {labelEl}
          <div className="space-y-2">
            {["Address Line 1", "Landmark", "City", "State", "Pincode"].map(
              (f) => (
                <input
                  key={f}
                  type="text"
                  disabled
                  placeholder={f}
                  className={inputClass}
                />
              ),
            )}
          </div>
          {helpEl}
        </div>
      );
    default:
      return (
        <div className="ff-field">
          {labelEl}
          <input
            readOnly
            placeholder={FIELD_TYPE_MAP[fieldType]?.label || fieldType}
            className={inputClass}
          />
          {helpEl}
        </div>
      );
  }
}

function SectionPreview({ section }) {
  const fields = (section.fields || []).filter((f) => f.enabled !== false);
  const isCommonSection = isCommonBasicSection(section);
  const sectionServices = getSectionServices(section);
  return (
    <div className="mb-5">
      <div className="mb-3 pb-2.5 border-b border-gray-200">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="m-0 text-lg font-bold text-gray-900">
            {section.sectionName}
          </h2>
          {!isCommonSection && sectionServices.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {sectionServices.map((srv) => (
                <span
                  key={srv}
                  className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                >
                  {srv}
                </span>
              ))}
            </div>
          )}
        </div>
        {section.sectionDescription && (
          <p className="m-0 mt-1 text-[13px] text-gray-500">
            {section.sectionDescription}
          </p>
        )}
      </div>
      <div className="ff-grid">
        {fields.map((field) => (
          <FieldPreview key={field.id} field={field} />
        ))}
        {fields.length === 0 && (
          <p className="text-xs text-gray-400 italic">
            No fields in this section.
          </p>
        )}
      </div>
    </div>
  );
}
function categorizeSections(sections) {
  const customer = [];
  const pet = [];
  let inPetCtx = false;

  (sections || []).forEach((s) => {
    const name = String(s.sectionName || "").toLowerCase();
    const explicitlyPet = /pet/.test(name) && !/parent/.test(name);
    const explicitlyCustomer =
      /customer|parent|address|contact|billing|required/.test(name);

    const hasPetFields = (s.fields || []).some((f) => {
      const txt = `${f.label || ""} ${f.mapsTo || ""}`.toLowerCase();
      return /\bpet\b|breed|vaccination|meal\s*type/.test(txt);
    });

    if (explicitlyPet || hasPetFields) {
      inPetCtx = true;
      pet.push(s);
    } else if (explicitlyCustomer) {
      inPetCtx = false;
      customer.push(s);
    } else if (inPetCtx) {
      pet.push(s);
    } else {
      customer.push(s);
    }
  });

  return { customerSections: customer, petSections: pet };
}

export default function FormPreviewPanel({
  formName,
  formDescription,
  sections,
  vendorName,
  vendorLogo,
  previewMode = "desktop",
}) {
  const [step, setStep] = useState(0); // 0 = customer, 1 = pet

    const topRef = useRef(null);

    useEffect(() => {
      if (!topRef.current) return;
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [step]);

  const { customerSections, petSections } = useMemo(
    () => categorizeSections(sections),
    [sections],
  );

  const visibleCustomer = customerSections.filter(
    (s) => (s.fields || []).some((f) => f.enabled !== false) || s.sectionName,
  );
  const visiblePet = petSections.filter(
    (s) => (s.fields || []).some((f) => f.enabled !== false) || s.sectionName,
  );

  const hasPetStep = visiblePet.length > 0;
  const totalSteps = hasPetStep ? 2 : 1;
  const currentSections = step === 0 ? visibleCustomer : visiblePet;
  const stepLabels = hasPetStep ? ["Pet parent", "Pet"] : ["Pet parent"];

    return (
      <div
        ref={topRef}
        className={`ff-preview ff-preview--${previewMode}`}
        style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
      >
        <header className="ff-preview-top">
          <div className="ff-preview-top-inner">
            {(vendorLogo || vendorName) && (
              <div className="flex items-center gap-2.5 mb-3 min-w-0">
                {vendorLogo ? (
                  <img
                    src={vendorLogo}
                    alt={vendorName || "Logo"}
                    className="w-[30px] h-[30px] object-contain rounded-lg bg-white shrink-0"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="w-[30px] h-[30px] rounded-lg bg-orange-500 text-white text-xs font-bold flex items-center justify-center shrink-0">
                    {(vendorName || "P").trim().charAt(0).toUpperCase()}
                  </div>
                )}
                {vendorName && (
                  <h2 className="m-0 text-[15px] font-semibold text-gray-900 shrink-0">
                    {vendorName}
                  </h2>
                )}

                {/* 👇 Form name on the same row, pushed to the right */}
                {formName && (
                  <span className="ml-auto text-[12px] font-semibold text-gray-500 whitespace-nowrap truncate">
                    {formName}
                  </span>
                )}
              </div>
            )}

            <ol className="flex items-center gap-0 list-none m-0 p-0 pb-3 overflow-x-auto">
              {stepLabels.map((label, i, arr) => (
                <li
                  key={label}
                  className={`flex items-center gap-2 text-xs font-medium whitespace-nowrap ${
                    i === step ? "text-gray-900 font-semibold" : "text-gray-400"
                  } ${i < arr.length - 1 ? "flex-1 min-w-[36px]" : ""}`}
                >
                  <span
                    className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-[11px] font-semibold ${
                      i <= step
                        ? "bg-[#EA580C] text-white"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {i < step ? "✓" : i + 1}
                  </span>
                  {label}
                  {i < arr.length - 1 && (
                    <span className="flex-1 h-0.5 bg-gray-200 rounded mx-2" />
                  )}
                </li>
              ))}
            </ol>
          </div>
        </header>

        <div className="ff-preview-main">
          <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-sm">
            {currentSections.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                {step === 0
                  ? "No customer fields to show yet."
                  : "No pet fields to show yet."}
              </p>
            ) : (
              currentSections.map((section, i) => (
                <SectionPreview key={section.id || i} section={section} />
              ))
            )}
          </div>

          {(visibleCustomer.length > 0 || visiblePet.length > 0) && (
            <div className="mt-4 flex gap-3">
              {step > 0 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="py-3.5 px-6 bg-white border border-[#EA580C] text-[#EA580C] rounded-lg text-[15px] font-semibold cursor-pointer"
                >
                  ← Back
                </button>
              )}

              {step < totalSteps - 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step + 1)}
                  className="flex-1 py-3.5 px-6 bg-[#EA580C] text-white rounded-lg text-[15px] font-semibold cursor-pointer"
                >
                  Continue to pet details
                </button>
              )}

              {step === totalSteps - 1 && (
                <button
                  type="button"
                  disabled
                  className="flex-1 py-3.5 px-6 bg-[#EA580C] text-white rounded-lg text-[15px] font-semibold opacity-90 cursor-default"
                >
                  Submit
                </button>
              )}
            </div>
          )}
        </div>

        <style>{`
        .ff-preview { background: #F7F8FA; color: #111827; border-radius: 16px; overflow: hidden; border: 1px solid #E5E7EB; }
        .ff-preview-top { background: rgba(255,255,255,.92); border-bottom: 1px solid #E5E7EB; }
        .ff-preview-top-inner { max-width: 800px; margin: 0 auto; padding: 12px 16px 0; }
        .ff-preview-main { max-width: 800px; margin: 0 auto; padding: 20px 16px 28px; }
        .ff-preview .ff-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 16px; }
        .ff-preview .ff-field.ff-full { grid-column: 1 / -1; }

        .ff-preview--mobile .ff-grid {
          grid-template-columns: 1fr;
          gap: 12px;
        }
        .ff-preview--mobile .ff-field,
        .ff-preview--mobile .ff-field.ff-short,
        .ff-preview--mobile .ff-field.ff-full {
          grid-column: 1 / -1;
        }

        @media (max-width: 640px) {
          .ff-preview .ff-grid { grid-template-columns: 1fr; }
        }
      `}</style>
      </div>
    );
}
